import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import type { TrainingWeek } from '../types/database.tables'
import { formatDateForComparison } from '../utils/dateUtils'

export interface UseTrainingWeeksReturn {
  trainingWeek: TrainingWeek | null
  isLoading: boolean
  error: string | null
  loadTrainingWeek: (startDate: Date) => Promise<void>
  updateTrainingWeek: (
    updates: Partial<Omit<TrainingWeek, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
  ) => Promise<void>
  createTrainingWeek: (
    data: Omit<TrainingWeek, 'id' | 'user_id' | 'created_at' | 'updated_at'>
  ) => Promise<TrainingWeek>
  getPreviousWeek: (startDate: Date) => Promise<TrainingWeek | null>
}

export const useTrainingWeeks = (): UseTrainingWeeksReturn => {
  const { user } = useAuth()
  const [trainingWeek, setTrainingWeek] = useState<TrainingWeek | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Function to load training week for a specific date
  const loadTrainingWeek = useCallback(
    async (startDate: Date): Promise<void> => {
      if (!user) {
        setTrainingWeek(null)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const startDateString = formatDateForComparison(startDate)

        const { data, error: dbError } = await supabase
          .from('training_weeks')
          .select('*')
          .eq('user_id', user.id)
          .eq('start_date', startDateString)
          .maybeSingle()

        if (dbError) {
          console.error('Error loading training week:', dbError)
          throw dbError
        }

        setTrainingWeek(data)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load training week'
        console.error('Failed to load training week:', err)
        setError(errorMessage)
        setTrainingWeek(null)
      } finally {
        setIsLoading(false)
      }
    },
    [user]
  )

  // Function to create a new training week
  const createTrainingWeek = useCallback(
    async (data: Omit<TrainingWeek, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<TrainingWeek> => {
      if (!user) {
        throw new Error('User not authenticated')
      }

      try {
        const newTrainingWeek = {
          ...data,
          user_id: user.id,
        }

        const { data: createdData, error: dbError } = await supabase
          .from('training_weeks')
          .insert(newTrainingWeek)
          .select()
          .single()

        if (dbError) {
          console.error('Error creating training week:', dbError)
          throw dbError
        }

        console.log('Successfully created training week:', createdData)
        setTrainingWeek(createdData)

        return createdData
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create training week'
        console.error('Failed to create training week:', err)
        setError(errorMessage)
        throw err
      }
    },
    [user]
  )

  // Function to update existing training week
  const updateTrainingWeek = useCallback(
    async (updates: Partial<Omit<TrainingWeek, 'id' | 'user_id' | 'created_at' | 'updated_at'>>): Promise<void> => {
      if (!user || !trainingWeek) {
        throw new Error('User not authenticated or no training week to update')
      }

      try {
        const { data, error: dbError } = await supabase
          .from('training_weeks')
          .update(updates)
          .eq('id', trainingWeek.id)
          .eq('user_id', user.id)
          .select()
          .single()

        if (dbError) {
          console.error('Error updating training week:', dbError)
          throw dbError
        }

        console.log('Successfully updated training week:', data)
        setTrainingWeek(data)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update training week'
        console.error('Failed to update training week:', err)
        setError(errorMessage)
        throw err
      }
    },
    [user, trainingWeek]
  )

  // Function to get the most recent training week before the given date
  const getPreviousWeek = useCallback(
    async (startDate: Date): Promise<TrainingWeek | null> => {
      if (!user) {
        return null
      }

      try {
        const startDateString = formatDateForComparison(startDate)

        const { data, error: dbError } = await supabase
          .from('training_weeks')
          .select('*')
          .eq('user_id', user.id)
          .lt('start_date', startDateString)
          .order('start_date', { ascending: false })
          .limit(1)
          .maybeSingle()

        if (dbError) {
          console.error('Error loading previous training week:', dbError)
          return null
        }

        return data
      } catch (err) {
        console.error('Failed to load previous training week:', err)
        return null
      }
    },
    [user]
  )

  // Function to create training week if it doesn't exist, or get existing one
  const getOrCreateTrainingWeek = useCallback(
    async (
      startDate: Date,
      defaults?: Partial<Omit<TrainingWeek, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'start_date'>>
    ): Promise<TrainingWeek> => {
      await loadTrainingWeek(startDate)

      if (trainingWeek) {
        return trainingWeek
      }

      // Get previous week to increment from
      const previousWeek = await getPreviousWeek(startDate)

      // Create new training week with data from previous week or defaults
      const newTrainingWeekData = {
        start_date: formatDateForComparison(startDate),
        week_in_plan: previousWeek ? previousWeek.week_in_plan + 1 : 1,
        training_phase: previousWeek ? previousWeek.training_phase : ('build' as const),
        target_hours: previousWeek ? previousWeek.target_hours : 8.0,
        comments: null,
        ...defaults,
      }

      return await createTrainingWeek(newTrainingWeekData)
    },
    [loadTrainingWeek, trainingWeek, createTrainingWeek, getPreviousWeek]
  )

  // Clear training week when user changes
  useEffect(() => {
    if (!user) {
      setTrainingWeek(null)
      setError(null)
    }
  }, [user])

  return {
    trainingWeek,
    isLoading,
    error,
    loadTrainingWeek,
    updateTrainingWeek,
    createTrainingWeek,
    getPreviousWeek,
    getOrCreateTrainingWeek,
  } as UseTrainingWeeksReturn & { getOrCreateTrainingWeek: typeof getOrCreateTrainingWeek }
}
